import queryString from 'query-string';
import { cookieStorage } from './cookies';
import runtimeConfig from '../config';

/** Encode a string to hex */
export const toHex = (str: string) => {
  return Array.from(str)
    .map((c) => c.charCodeAt(0).toString(16).padStart(2, '0'))
    .join('');
};

/** Decode a hex string */
export const hexDecode = (hex: string): string => {
  const input = hex.startsWith('0x') ? hex.slice(2) : hex;

  const bytes = new Uint8Array(
    (input.match(/.{1,2}/g) ?? []).map((byte) => parseInt(byte, 16)),
  );

  return new TextDecoder().decode(bytes);
};

/** Parse the state query string returned by Oauth flow to a JSON object */
export const parseStateParam = (state: string) => {
  const stateParts = state.split('-');

  const stateString =
    stateParts.length > 1
      ? atob(hexDecode(stateParts[1]))
      : atob(hexDecode(stateParts[0]));

  if (stateString.startsWith('{')) {
    return JSON.parse(stateString);
  }

  return {};
};

/**
 * Return the redirectUri from the query string, state param or cookie. Defaults to environment variables.
 */
export const getRedirectUri = (
  search: string,
  defaultUri?: string,
): string | undefined => {
  const query = new URLSearchParams(search);

  const queryParam = query.get('redirect_uri');
  if (queryParam) {
    return queryParam;
  }

  const state = query.get('state');
  if (state) {
    const stateObj = parseStateParam(state);
    return stateObj.redirect_uri;
  }

  return (
    cookieStorage.getItem('last-logout-redirectUri') ||
    defaultUri ||
    runtimeConfig.REDIRECT_URI ||
    process.env.REACT_APP_REDIRECT_URI
  );
};

/**
 * Return the clientId from the query string, state param or cookie. Defaults to environment variables.
 */
export const getClientId = (search: string): string | undefined => {
  const query = new URLSearchParams(search);

  const clientIdString = query.get('clientId');
  if (clientIdString) {
    return clientIdString;
  }

  const stateString = query.get('state');
  if (stateString && typeof stateString === 'string') {
    const stateObj = parseStateParam(stateString);
    return stateObj.clientId;
  }

  return (
    cookieStorage.getItem('last-logout-clientId') ||
    runtimeConfig.COGNITO_CLIENT_ID ||
    process.env.REACT_APP_COGNITO_CLIENT_ID
  );
};

export const parseQuery = (query: string) => {
  return queryString.parse(query);
};

export const stringifyQuery = (query: Record<string, unknown>) => {
  return queryString.stringify(query);
};
